/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// ============================================================================
// Preorder / Sold-out / Add-to-cart label manager + BIS visibility (Symmetry)
// - PREORDER when: trackInventory && stockQty <= 0 && continueSelling === true
// - SOLD OUT when: variant.available === false
// - Default "Add to cart" when: available && (stock > 0 || !trackInventory || !continue)
// ============================================================================

(function () {
  console.log('[ATC/PREORDER] init');

  // ---------- small utils ----------
  function waitForElement(selector, root = document, maxWait = 5000) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      (function check() {
        const el = root.querySelector(selector);
        if (el) return resolve(el);
        if (Date.now() - start >= maxWait) return reject(new Error(`Timeout waiting for ${selector}`));
        requestAnimationFrame(check);
      })();
    });
  }
  function getRootFromForm(formEl) {
    return formEl?.closest('.product-form') || document;
  }
  function parsePickerData(root) {
    const jsonEl = root.querySelector('variant-picker script[type="application/json"]');
    if (!jsonEl || !jsonEl.textContent) return null;
    try {
      return JSON.parse(jsonEl.textContent.trim());
    } catch (e) {
      console.warn('[ATC/PREORDER] JSON parse error in <variant-picker> block:', e);
      return null;
    }
  }
  function getCurrentVariant(formEl, variants) {
    const id = formEl?.querySelector('input[name="id"]')?.value;
    if (!id || !variants) return null;
    return variants.find(v => String(v.id) === String(id)) || null;
  }
  function coerceInt(x) {
    const n = parseInt(String(x).replace(/[^\d-]/g, ''), 10);
    return Number.isFinite(n) ? n : null;
  }
  // Fallback: your DOM has #variant-quantity-<id> with "3 in stock"
  function getQtyFromDomFallback(root, variantId) {
    const el = root.querySelector(`#variant-quantity-${variantId}`);
    if (!el) return null;
    return coerceInt(el.textContent);
  }

  // Derive inventory facts (prefers JSON "formatted", falls back to variant fields then DOM)
  function deriveInventoryFacts(variant, formattedMap, root) {
    const meta = formattedMap?.[String(variant.id)] || {};
    const hasTrackFlag = typeof meta.trackInventory === 'boolean';
    const hasQty = typeof meta.inventoryQuantity === 'number';

    const trackInventory =
      hasTrackFlag ? meta.trackInventory :
      (variant.inventory_management != null);

    let qty =
      hasQty ? meta.inventoryQuantity :
      (typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null);

    if ((qty == null || Number.isNaN(qty)) && root) {
      qty = getQtyFromDomFallback(root, variant.id);
    }

    const continueSelling =
      typeof meta.continueSelling === 'boolean' ? meta.continueSelling :
      (variant.inventory_policy === 'continue');

    const available =
      typeof variant.available === 'boolean'
        ? variant.available
        : (trackInventory ? (qty || 0) > 0 || continueSelling : true);

    return { trackInventory, qty, continueSelling, available, meta };
  }

  // ---------- BIS visibility ----------
  async function toggleBIS(formEl, show) {
    const root = getRootFromForm(formEl);
    try {
      const btn = await waitForElement('.klaviyo-bis-trigger', root, 5000);
      if (show) {
        btn.style.display = '';
        btn.removeAttribute('aria-hidden');
        console.log('[BIS] Shown (variant not purchasable).');
      } else {
        btn.style.display = 'none';
        btn.setAttribute('aria-hidden', 'true');
        console.log('[BIS] Hidden (variant purchasable).');
      }
    } catch (err) {
      console.warn('[BIS] Button not found in time:', err.message);
    }
  }

  // ---------- ATC label/state ----------
  function updateATC(formEl, state) {
    const btn = formEl?.querySelector('.add-to-cart');
    if (!btn) {
      console.warn('[ATC/PREORDER] No .add-to-cart button in scope');
      return;
    }
    if (!btn.dataset.originalText) {
      // capture original text once
      btn.dataset.originalText = btn.getAttribute('data-add-to-cart-text') || btn.textContent.trim() || 'Add to cart';
    }

    const setDisabled = (flag) => {
      if (flag) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('disabled');
        btn.setAttribute('aria-disabled', 'false');
      }
    };

    if (!state) {
      btn.textContent = 'Unavailable';
      setDisabled(true);
      console.log('[ATC/PREORDER] Set to Unavailable');
      return;
    }

    const { available, trackInventory, qty, continueSelling } = state;

    if (!available) {
      btn.textContent = 'Sold out';
      setDisabled(true);
      console.log('[ATC/PREORDER] Set to SOLD OUT (unavailable)');
      return;
    }

    // available === true
    if (trackInventory && (qty == null || qty <= 0) && continueSelling) {
      btn.textContent = 'PREORDER';
      setDisabled(false);
      console.log('[ATC/PREORDER] Set to PREORDER (qty<=0 & continueSelling)');
    } else {
      btn.textContent = btn.dataset.originalText || 'Add to cart';
      setDisabled(false);
      console.log('[ATC/PREORDER] Set to Add to cart (normal)');
    }
  }

  // ---------- central handler ----------
  function handleVariantChange(formEl, variant) {
    const root = getRootFromForm(formEl);
    const data = parsePickerData(root);
    const facts = variant ? deriveInventoryFacts(variant, data?.formatted, root) : null;

    if (variant) {
      console.log('[ATC/PREORDER] Variant selected:', {
        id: variant.id,
        title: variant.title,
        available: facts?.available,
        qty: facts?.qty,
        trackInventory: facts?.trackInventory,
        continueSelling: facts?.continueSelling
      });
    } else {
      console.warn('[ATC/PREORDER] No variant resolved for this form');
    }

    // ATC label/state
    updateATC(formEl, facts);

    // BIS visibility: show only when NOT purchasable
    // (Shopify marks available=false when not purchasable; PREORDER is still purchasable)
    toggleBIS(formEl, !(facts?.available));
  }

  // ---------- wire up events ----------
  document.addEventListener('on:variant:change', (evt) => {
    const formEl = evt?.detail?.form || document.querySelector('form.js-product-form');
    const root = getRootFromForm(formEl);
    const data = parsePickerData(root);
    const variant = evt?.detail?.variant || getCurrentVariant(formEl, data?.variants);
    console.log('[ATC/PREORDER] on:variant:change', { hasDetailVariant: !!evt?.detail?.variant, id: variant?.id });
    handleVariantChange(formEl, variant);
  });

  // Initial pass for pre-selected variant(s)
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('form.js-product-form').forEach((formEl) => {
      const root = getRootFromForm(formEl);
      const data = parsePickerData(root);
      const variant = getCurrentVariant(formEl, data?.variants);
      console.log('[ATC/PREORDER] DOMContentLoaded initial', { id: variant?.id });
      handleVariantChange(formEl, variant);
    });
  });

  // Fallback: when hidden variant id input changes without the theme event
  document.addEventListener('change', (e) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    if (e.target.name !== 'id') return;
    const formEl = e.target.closest('form.js-product-form');
    if (!formEl) return;
    const root = getRootFromForm(formEl);
    const data = parsePickerData(root);
    const variant = getCurrentVariant(formEl, data?.variants);
    console.log('[ATC/PREORDER] Hidden input id changed → fallback', { id: variant?.id });
    handleVariantChange(formEl, variant);
  });
})();
